import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authGet } from '../utils/auth';

// Shared admin gate.
//
// Every admin page repeated the same block: GET /auth/me, check role==='admin',
// otherwise redirect to /admin/login. Two pages (AdminDashboard, PendingAccess)
// also trusted client-supplied `location.state.user` and skipped the server
// check entirely — an auth bypass. This hook is the single, server-verified gate.
//
// Usage:
//   const { user, checking } = useRequireAdmin();
//   if (checking) return <Spinner/>;   // optional
//
// Returns { user, checking }. On a failed/absent admin session it redirects to
// /admin/login and leaves user=null.

export function useRequireAdmin(redirectTo = '/admin/login') {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await authGet('/auth/me');
        const u = res.data;
        if (!active) return;
        if (u && u.role === 'admin') {
          setUser(u);
        } else {
          navigate(redirectTo);
        }
      } catch (err) {
        if (active) navigate(redirectTo);
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => { active = false; };
  }, [navigate, redirectTo]);

  return { user, checking };
}

export default useRequireAdmin;
